<template>
  <div class="upload-container">
    <!-- 上传区域 -->
    <div class="upload-area" v-show="!currentImage">
      <el-upload
        class="image-uploader"
        ref="imageUpload"
        :show-file-list="false"
        :http-request="handleUpload"
        :before-upload="beforeUpload"
        :accept="accept || 'image/*'"
        action="#"
      >
        <div class="upload-trigger" v-if="!currentImage">
          <i class="el-icon-plus"></i>
          <div class="upload-text">封面图</div>
        </div>
      </el-upload>
    </div>

    <!-- 图片展示区域 -->
    <div class="image-display" v-if="currentImage">
      <div 
        class="image-item"
        @mouseenter="isHovered = true"
        @mouseleave="isHovered = false"
      >
        <div class="image-wrapper">
          <!-- 显示图片或加载状态 -->
          <div v-if="currentImage.uploading" class="upload-placeholder">
            <img-load v-if="currentImage.previewUrl" :src="currentImage.previewUrl" alt="预览图片" class="preview-image" />
            <div v-else class="loading-placeholder">
              <i class="el-icon-picture-outline"></i>
              <div class="loading-text">正在上传...</div>
            </div>
          </div>
          <img-load v-else :src="getImageUrl(currentImage)" alt="上传的图片" class="uploaded-image" />
          
          <!-- 悬浮遮罩层 -->
          <div class="image-overlay" :class="{ 'show': isHovered && !currentImage.uploading }">
            <div class="overlay-actions">
              <el-tooltip content="预览" placement="top">
                <i class="el-icon-zoom-in action-btn" @click="previewImage"></i>
              </el-tooltip>
              <el-tooltip content="重新上传" placement="top">
                <i class="el-icon-edit action-btn" @click="reuploadImage"></i>
              </el-tooltip>
              <el-tooltip content="删除" placement="top">
                <i class="el-icon-delete action-btn delete-btn" @click="deleteImage"></i>
              </el-tooltip>
            </div>
          </div>

          <!-- 上传进度 -->
          <div class="upload-progress" v-if="currentImage.uploading">
            <el-progress 
              :percentage="currentImage.progress" 
              :show-text="true"
              :format="formatProgress"
            ></el-progress>
          </div>
        </div>
      </div>
    </div>

    <!-- 图片预览对话框 -->
    <el-dialog 
      title="图片预览" 
      :visible.sync="previewVisible" 
      width="80%"
      :append-to-body="true"
      :close-on-click-modal="true"
      class="image-preview-dialog"
    >
      <div class="preview-container">
        <div class="preview-image-wrapper">
          <img-load :src="previewImageUrl" alt="预览图片" class="preview-image" />
          <!-- <img :src="previewImageUrl" alt="预览图片" class="preview-image" @load="onPreviewLoad" @error="onPreviewError" /> -->
        </div>
        <!-- <div class="preview-actions">
          <el-button @click="previewVisible = false">关闭</el-button>
        </div> -->
      </div>
    </el-dialog>

    <!-- 隐藏的重新上传input -->
    <input 
      ref="reuploadInput" 
      type="file" 
      :accept="accept || 'image/*'" 
      style="display: none;" 
      @change="handleReupload"
    />
  </div>
</template>

<script>
import API from '@/api';

export default {
  name: 'UploadImage',
  props: {
    objKey: String,
    imageUrl: String,
    accept: {
      type: String,
      default: 'image/*'
    },
    maxSize: {
      type: Number,
      default: 5 // MB
    }
  },
  data() {
    return {
      currentImage: null,
      isHovered: false,
      previewVisible: false,
      previewImageUrl: '',
      previewLoading: false,
      previewError: false
    };
  },
  watch: {
    imageUrl: {
      handler(newVal) {
        this.initImage(newVal);
      },
      immediate: true
    }
  },
  methods: {
    // 初始化图片
    initImage(imageUrl) {
      if (!imageUrl) {
        this.currentImage = null;
        return;
      }

      this.currentImage = {
        url: imageUrl,
        uploading: false,
        progress: 0,
        previewUrl: null
      };
    },

    // 获取图片URL
    getImageUrl(image) {
      if (!image || !image.url) return '';
      return this.setImages(image.url);
    },

    // 设置图片URL（保持原有逻辑）
    setImages(val) {
      if (!val) return '';
      
      // 判断是否拥有域名 如果有 去掉域名 字符串第一位是'/'
      if (val.indexOf('http') === 0) {
        val = '/' + val.split('/').slice(3).join('/');
      } else if (val.indexOf('/') !== 0) {
        val = '/' + val;
      }
      
      // 根据去掉域名后的数据进行匹配
      let strValue = '';
      let data = JSON.parse(JSON.parse(localStorage.getItem('imageUrl')));
      for (const key in data) {
        if (key.indexOf('/') !== 0 && key !== '*') {
          if (val.indexOf('/' + key + '/') === 0) {
            strValue = data[key];
          }
        } else if (key.indexOf('/') === 0 && key !== '*') {
          if (val.indexOf(key + '/') === 0) {
            strValue = data[key];
          }
        }
      }
      if (strValue) {
        return strValue + val;
      } else {
        return data['*'] + val;
      }
    },

    // 创建文件预览URL
    createPreviewUrl(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve(e.target.result);
        };
        reader.onerror = () => {
          resolve(null);
        };
        reader.readAsDataURL(file);
      });
    },

    // 上传前验证
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/');
      const isLtMaxSize = file.size / 1024 / 1024 < this.maxSize;

      if (!isImage) {
        this.$message.error('只能上传图片文件！');
        return false;
      }
      if (!isLtMaxSize) {
        this.$message.error(`图片大小不能超过 ${this.maxSize}MB！`);
        return false;
      }

      return true;
    },

    // 处理上传
    async handleUpload(option) {
      const { file } = option;
      
      // 创建预览URL
      const previewUrl = await this.createPreviewUrl(file);
      
      // 设置上传状态
      this.currentImage = {
        url: '',
        uploading: true,
        progress: 0,
        file: file,
        previewUrl: previewUrl
      };

      try {
        const formData = new FormData();
        formData.append('upload', file);

        // 模拟上传进度
        const progressInterval = setInterval(() => {
          if (this.currentImage.progress < 90) {
            this.currentImage.progress += Math.random() * 30;
          }
        }, 200);

        const { data } = await API.fileUpload(formData);
        
        clearInterval(progressInterval);
        this.currentImage.progress = 100;
        
        setTimeout(() => {
          this.currentImage.uploading = false;
          this.currentImage.url = data.coverImg;
          this.currentImage.previewUrl = null; // 清除临时预览
          this.emitChange();
        }, 500);

      } catch (error) {
        console.error('上传失败:', error);
        this.$message.error('上传失败，请重试！');
        this.currentImage = null;
      }
    },

    // 预览图片
    previewImage() {
      if (this.currentImage.uploading && this.currentImage.previewUrl) {
        // 如果正在上传，显示临时预览
        this.previewImageUrl = this.currentImage.previewUrl;
      } else if (this.currentImage.url) {
        // 显示正式图片
        this.previewImageUrl = this.getImageUrl(this.currentImage);
      } else {
        this.$message.warning('图片还未上传完成');
        return;
      }
      
      this.previewVisible = true;
      this.previewLoading = true;
      this.previewError = false;
    },

    // 预览图片加载完成
    onPreviewLoad() {
      this.previewLoading = false;
      this.previewError = false;
    },

    // 预览图片加载失败
    onPreviewError() {
      this.previewLoading = false;
      this.previewError = true;
    },

    // 下载图片
    downloadImage() {
      if (!this.previewImageUrl) return;
      
      const link = document.createElement('a');
      link.href = this.previewImageUrl;
      link.download = `image_${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // 重新上传图片
    reuploadImage() {
      this.$refs.reuploadInput.click();
    },

    // 处理重新上传
    async handleReupload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // 验证文件
      if (!this.beforeUpload(file)) {
        return;
      }

      // 创建预览URL
      const previewUrl = await this.createPreviewUrl(file);

      // 设置上传状态
      this.currentImage = {
        url: this.currentImage ? this.currentImage.url : '',
        uploading: true,
        progress: 0,
        file: file,
        previewUrl: previewUrl
      };

      try {
        const formData = new FormData();
        formData.append('upload', file);

        // 模拟上传进度
        const progressInterval = setInterval(() => {
          if (this.currentImage.progress < 90) {
            this.currentImage.progress += Math.random() * 30;
          }
        }, 200);

        const { data } = await API.fileUpload(formData);
        
        clearInterval(progressInterval);
        this.currentImage.progress = 100;
        
        setTimeout(() => {
          this.currentImage.uploading = false;
          this.currentImage.url = data.coverImg;
          this.currentImage.previewUrl = null; // 清除临时预览
          this.emitChange();
          this.$message.success('图片更新成功！');
        }, 500);

      } catch (error) {
        console.error('重新上传失败:', error);
        this.$message.error('重新上传失败，请重试！');
        // 恢复之前的状态
        this.currentImage.uploading = false;
        this.currentImage.progress = 0;
        this.currentImage.previewUrl = null;
      }

      // 清空input值，允许选择相同文件
      event.target.value = '';
    },

    // 删除图片
    deleteImage() {
      this.$confirm('确定要删除这张图片吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        this.currentImage = null;
        this.emitChange();
        this.$message.success('删除成功！');
      }).catch(() => {
        // 取消删除
      });
    },

    // 格式化进度显示
    formatProgress(percentage) {
      return `${Math.round(percentage)}%`;
    },

    // 发出变化事件
    emitChange() {
      const url = this.currentImage && this.currentImage.url && !this.currentImage.uploading 
        ? this.currentImage.url 
        : '';
      
      this.$emit('uploadFileUrl', this.objKey, url, false);
      this.$emit('change', url);
    }
  }
};
</script>

<style scoped lang="scss">
.upload-container {
  width: 100%;
  
  .upload-area {
    margin-bottom: 16px;
    
    .image-uploader {
      .upload-trigger {
        width: 100px;
        height: 100px;
        border: 2px dashed #d9d9d9;
        border-radius: 6px;
        cursor: pointer;
        position: relative;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        transition: border-color 0.3s;
        background-color: #fafafa;
        
        &:hover {
          border-color: #409EFF;
          color: #409EFF;
        }
        
        .el-icon-plus {
          font-size: 28px;
          color: #8c939d;
          margin-bottom: 8px;
        }
        
        .upload-text {
          font-size: 14px;
          color: #8c939d;
        }
      }
    }
  }
  
  .image-display {
    .image-item {
      position: relative;
      width: 100px;
      height: 100px;
      border-radius: 6px;
      overflow: hidden;
      border: 1px solid #e4e7ed;
      transition: all 0.3s;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
      
      .image-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
        
        .uploaded-image, .preview-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .upload-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f5f7fa;

          .preview-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            opacity: 0.7;
          }

          .loading-placeholder {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: #909399;

            .el-icon-picture-outline {
              font-size: 32px;
              margin-bottom: 8px;
            }

            .loading-text {
              font-size: 12px;
            }
          }
        }
        
        .image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s;
          
          &.show {
            opacity: 1;
          }
          
          .overlay-actions {
            display: flex;
            gap: 5px;
            
            .action-btn {
              font-size: 16px;
              color: white;
              cursor: pointer;
              padding: 4px;
              border-radius: 50%;
              transition: all 0.3s;
              
              &:hover {
                background: rgba(255, 255, 255, 0.2);
                transform: scale(1.1);
              }
              
              &.delete-btn:hover {
                color: #f56c6c;
                background: rgba(245, 108, 108, 0.2);
              }
            }
          }
        }
        
        .upload-progress {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 8px;
          background: rgba(255, 255, 255, 0.95);
          
          ::v-deep .el-progress__text {
            font-size: 12px !important;
          }
        }
      }
    }
  }
}

// 预览对话框样式
::v-deep .image-preview-dialog {
  .el-dialog {
    margin-top: 5vh !important;
    
    .el-dialog__body {
      padding: 20px;
    }
  }
}

.preview-container {
  .preview-image-wrapper {
    position: relative;
    text-align: center;
    margin-bottom: 20px;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .preview-image {
      max-width: 100%;
      height: auto;
      // max-height: 50vh;
      object-fit: contain;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .preview-loading, .preview-error {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #909399;
      font-size: 14px;

      .el-icon-loading, .el-icon-warning-outline {
        font-size: 32px;
        margin-bottom: 8px;
      }

      .el-icon-loading {
        animation: rotating 2s linear infinite;
      }
    }

    .preview-error {
      color: #f56c6c;
    }
  }

  .preview-actions {
    text-align: center;
    
    .el-button {
      margin: 0 8px;
    }
  }
}

// 对话框样式优化
::v-deep .el-dialog {
  .el-dialog__header {
    padding: 20px 20px 10px;
    border-bottom: 1px solid #ebeef5;
    
    .el-dialog__title {
      font-weight: 600;
    }
  }
  
  .el-dialog__body {
    padding: 20px;
  }
}

// 工具提示样式
::v-deep .el-tooltip__popper {
  font-size: 12px;
}

// 旋转动画
@keyframes rotating {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
